import { useMemo, useState } from "react"
import { Box, HStack, Spinner, Stack } from "@chakra-ui/react"
import { Text } from "@/components/ui"
import { useColorMode } from "@/contexts"
import { useIrrDashboard } from "@/hooks/irr/useIrrDashboard"
import { useFundCapitalCalls, useFundDistributions } from "@/hooks/irr"

const fmtMoney = (v: number) =>
  `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const fmtDate = (d: string) => {
  const date = new Date(d + "T00:00:00")
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

export const FundCashFlowsComponent = () => {
  const { colorMode } = useColorMode()
  const isDark = colorMode === "dark"
  const { data: funds, isLoading: fundsLoading } = useIrrDashboard()

  const [selectedFundId, setSelectedFundId] = useState<number | null>(null)

  // Auto-select first fund once loaded
  const activeFundId = selectedFundId ?? funds[0]?.fundId ?? null
  const activeFund = funds.find((f) => f.fundId === activeFundId)

  const { data: capitalCalls = [], isLoading: callsLoading } = useFundCapitalCalls(activeFundId)
  const { data: distributions = [], isLoading: distsLoading } = useFundDistributions(activeFundId)

  const isLoading = fundsLoading || callsLoading || distsLoading

  const sortedCalls = useMemo(
    () => [...capitalCalls].sort((a, b) => a.callDate.localeCompare(b.callDate)),
    [capitalCalls]
  )

  const sortedDists = useMemo(
    () => [...distributions].sort((a, b) => a.distributionDate.localeCompare(b.distributionDate)),
    [distributions]
  )

  const totalCalls = useMemo(() => sortedCalls.reduce((s, c) => s + c.amount, 0), [sortedCalls])
  const totalDists = useMemo(() => sortedDists.reduce((s, d) => s + d.amount, 0), [sortedDists])

  const axisColor = isDark ? "#9ca3af" : "#6b7280"
  const gridColor = isDark ? "#374151" : "#e5e7eb"

  if (fundsLoading) {
    return (
      <Box display="flex" justifyContent="center" pt={20}>
        <Spinner size="xl" />
      </Box>
    )
  }

  if (!funds.length) {
    return (
      <Box textAlign="center" pt={20}>
        <Text color="text.muted">No fund data available.</Text>
      </Box>
    )
  }

  return (
    <Stack gap={6} pb={8}>
      {/* Fund selector buttons */}
      <HStack gap={2} flexWrap="wrap">
        {funds.map((f) => {
          const isActive = f.fundId === activeFundId
          return (
            <Box
              key={f.fundId}
              px={4}
              py={2}
              cursor="pointer"
              borderRadius="md"
              bg={isActive ? "blue.500" : "card.bg"}
              color={isActive ? "white" : "text.primary"}
              border="1px solid"
              borderColor={isActive ? "blue.500" : "border.default"}
              onClick={() => setSelectedFundId(f.fundId)}
              _hover={{ bg: isActive ? "blue.600" : "hover.bg" }}
              transition="all 0.15s"
            >
              <Text fontSize="sm" fontWeight={isActive ? "semibold" : "medium"}>
                {f.fundName}
              </Text>
            </Box>
          )
        })}
      </HStack>

      {/* Summary row */}
      {activeFund && (
        <HStack gap={4} flexWrap="wrap">
          <Box
            px={5}
            py={4}
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="xl"
            bg="card.bg"
            flex="1"
            minW="180px"
          >
            <Text fontSize="xs" color="text.muted" fontWeight="medium">Total Capital Calls</Text>
            <Text fontSize="2xl" fontWeight="bold" color="red.400">{fmtMoney(totalCalls)}</Text>
            <Text fontSize="xs" color="text.subtle">{sortedCalls.length} calls</Text>
          </Box>
          <Box
            px={5}
            py={4}
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="xl"
            bg="card.bg"
            flex="1"
            minW="180px"
          >
            <Text fontSize="xs" color="text.muted" fontWeight="medium">Total Distributions</Text>
            <Text fontSize="2xl" fontWeight="bold" color="green.500">{fmtMoney(totalDists)}</Text>
            <Text fontSize="xs" color="text.subtle">{sortedDists.length} distributions</Text>
          </Box>
          <Box
            px={5}
            py={4}
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="xl"
            bg="card.bg"
            flex="1"
            minW="180px"
          >
            <Text fontSize="xs" color="text.muted" fontWeight="medium">Net Cash Flow</Text>
            <Text
              fontSize="2xl"
              fontWeight="bold"
              color={totalDists - totalCalls >= 0 ? "green.500" : "red.400"}
            >
              {totalDists - totalCalls >= 0 ? "" : "("}
              {fmtMoney(Math.abs(totalDists - totalCalls))}
              {totalDists - totalCalls >= 0 ? "" : ")"}
            </Text>
            <Text fontSize="xs" color="text.subtle">Distributions − Calls</Text>
          </Box>
        </HStack>
      )}

      {isLoading && !fundsLoading ? (
        <Box display="flex" justifyContent="center" pt={10}>
          <Spinner size="lg" />
        </Box>
      ) : (
        <HStack align="start" gap={6} flexWrap={{ base: "wrap", xl: "nowrap" }}>
          {/* Capital Calls table */}
          <Box
            bg="card.bg"
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="xl"
            overflow="hidden"
            flex="1"
            minW="340px"
          >
            <Box px={6} py={4} borderBottomWidth="1px" borderColor="border.default">
              <Text fontSize="md" fontWeight="semibold">Capital Calls</Text>
              <Text fontSize="xs" color="text.muted">{sortedCalls.length} entries</Text>
            </Box>
            <Box overflowX="auto" maxH="600px" overflowY="auto">
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: isDark ? "#1e2a4a" : "#f8fafc", position: "sticky", top: 0, zIndex: 1 }}>
                    {["#", "Date", "Amount", "Memo"].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "10px 14px",
                          textAlign: h === "#" || h === "Memo" ? "left" : h === "Date" ? "left" : "right",
                          fontWeight: 600,
                          color: axisColor,
                          whiteSpace: "nowrap",
                          borderBottom: `1px solid ${gridColor}`,
                          fontSize: 11,
                          letterSpacing: "0.03em",
                          textTransform: "uppercase",
                          background: isDark ? "#1e2a4a" : "#f8fafc",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedCalls.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ padding: "20px 14px", textAlign: "center", color: axisColor }}>
                        No capital calls recorded
                      </td>
                    </tr>
                  ) : (
                    sortedCalls.map((call, i) => (
                      <tr
                        key={call.id}
                        style={{
                          background: isDark
                            ? i % 2 === 0 ? "#16213e" : "#1a2744"
                            : i % 2 === 0 ? "#ffffff" : "#f8fafc",
                        }}
                      >
                        <td style={{ padding: "10px 14px", color: axisColor, fontWeight: 500, fontSize: 12 }}>
                          {i + 1}
                        </td>
                        <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                          {fmtDate(call.callDate)}
                        </td>
                        <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 600, color: "#f87171" }}>
                          {fmtMoney(call.amount)}
                        </td>
                        <td style={{ padding: "10px 14px", color: axisColor, fontSize: 12, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {call.memo || "—"}
                        </td>
                      </tr>
                    ))
                  )}
                  {sortedCalls.length > 0 && (
                    <tr style={{ background: isDark ? "#1e2a4a" : "#f0f4f8", borderTop: `2px solid ${gridColor}` }}>
                      <td colSpan={2} style={{ padding: "10px 14px", fontWeight: 700, fontSize: 12 }}>
                        Total
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: "#f87171" }}>
                        {fmtMoney(totalCalls)}
                      </td>
                      <td />
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </Box>

          {/* Distributions table */}
          <Box
            bg="card.bg"
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="xl"
            overflow="hidden"
            flex="1"
            minW="340px"
          >
            <Box px={6} py={4} borderBottomWidth="1px" borderColor="border.default">
              <Text fontSize="md" fontWeight="semibold">Distributions</Text>
              <Text fontSize="xs" color="text.muted">{sortedDists.length} entries</Text>
            </Box>
            <Box overflowX="auto" maxH="600px" overflowY="auto">
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: isDark ? "#1e2a4a" : "#f8fafc", position: "sticky", top: 0, zIndex: 1 }}>
                    {["#", "Date", "Amount", "Memo"].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "10px 14px",
                          textAlign: h === "#" || h === "Memo" ? "left" : h === "Date" ? "left" : "right",
                          fontWeight: 600,
                          color: axisColor,
                          whiteSpace: "nowrap",
                          borderBottom: `1px solid ${gridColor}`,
                          fontSize: 11,
                          letterSpacing: "0.03em",
                          textTransform: "uppercase",
                          background: isDark ? "#1e2a4a" : "#f8fafc",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedDists.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ padding: "20px 14px", textAlign: "center", color: axisColor }}>
                        No distributions recorded
                      </td>
                    </tr>
                  ) : (
                    sortedDists.map((dist, i) => (
                      <tr
                        key={dist.id}
                        style={{
                          background: isDark
                            ? i % 2 === 0 ? "#16213e" : "#1a2744"
                            : i % 2 === 0 ? "#ffffff" : "#f8fafc",
                        }}
                      >
                        <td style={{ padding: "10px 14px", color: axisColor, fontWeight: 500, fontSize: 12 }}>
                          {i + 1}
                        </td>
                        <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                          {fmtDate(dist.distributionDate)}
                        </td>
                        <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 600, color: "#4ade80" }}>
                          {fmtMoney(dist.amount)}
                        </td>
                        <td style={{ padding: "10px 14px", color: axisColor, fontSize: 12, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {dist.memo || "—"}
                        </td>
                      </tr>
                    ))
                  )}
                  {sortedDists.length > 0 && (
                    <tr style={{ background: isDark ? "#1e2a4a" : "#f0f4f8", borderTop: `2px solid ${gridColor}` }}>
                      <td colSpan={2} style={{ padding: "10px 14px", fontWeight: 700, fontSize: 12 }}>
                        Total
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: "#4ade80" }}>
                        {fmtMoney(totalDists)}
                      </td>
                      <td />
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </Box>
        </HStack>
      )}
    </Stack>
  )
}
